import type { ReactElement } from 'react';
import { useMemo } from 'react';

import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchAlgoliaImportedHit } from '#/modules/search/algolia/types/SearchAlgoliaImportedHit';
import { SearchHitImage } from '#/modules/search/components/Hit/Image/SearchHitImage';
import {
  SearchHitCard,
  SearchHitHolder,
  SearchHitImageHolder,
} from '#/modules/search/components/Hit/SearchHit.styled';
import { SearchHitInteractiveContentWrapper } from '#/modules/search/components/Hit/SearchHitInteractiveContentWrapper';
import { getImportedListingUrl } from '#/modules/search/helpers/getImportedListingUrl';
import { getSearchHitElementId } from '#/modules/search/utils/getSearchHitElementId';
import type { DatetimeString } from '#/types/TypedDateString';
import { getWorkLocationString } from '#/utils/address/getWorkLocationString';

import { SearchImportedHitInfo } from './SearchImportedHitInfo';

type Props = {
  hit: SearchAlgoliaImportedHit;
  trackClick: (() => void) | null;
  showLabel: boolean;
  highlighted: boolean;
  application: {
    status: 'ACTIVE' | 'INACTIVE';
    created: DatetimeString;
  } | null;
  actions: ReactElement | null;
};

export function SearchImportedHit({
  hit,
  trackClick,
  showLabel,
  highlighted,
  application,
  actions,
}: Props) {
  const { i18n } = useI18n();

  const {
    name,
    city,
    state,
    country,
    url,
    startDate,
    startTime,
    endDate,
    endTime,
    timezone,
    source,
    locationType,
    imageUrl,
  } = hit;

  const location = useMemo(
    () =>
      city !== undefined && state !== undefined && country !== undefined
        ? getWorkLocationString({
            i18n,
            remoteOptions: {
              remoteZone: null,
              remoteCountry: country,
              remoteState: state,
            },
            address: {
              city,
              country,
              stateCode: state,
            },
          })
        : null,
    [i18n, city, country, state],
  );

  const orgName = hit.source === 'GOLDEN' ? hit.orgName : null;

  return (
    <SearchHitHolder
      id={getSearchHitElementId(hit.objectID)}
      data-qa-id="search-result"
    >
      <SearchHitCard>
        <SearchHitInteractiveContentWrapper
          actions={actions}
          highlighted={Boolean(highlighted)}
          listingLinkData={{
            type: 'link',
            href: getImportedListingUrl(url),
            target: '_blank',
          }}
          onClick={() => trackClick?.()}
        >
          <SearchHitImageHolder $hiddenOnMobile>
            <SearchHitImage
              isImported
              imageUrl={imageUrl}
              image={null}
              source={source}
              name={name}
              logo={null}
            />
          </SearchHitImageHolder>

          <SearchImportedHitInfo
            application={application}
            startDate={startDate}
            startTime={startTime}
            endDate={endDate}
            endTime={endTime}
            timezone={timezone}
            isPostedAnonymously={false}
            location={location}
            locationType={locationType}
            name={name}
            orgName={orgName}
            orgType={null}
            showLabel={showLabel}
            source={source}
          />
        </SearchHitInteractiveContentWrapper>
      </SearchHitCard>
    </SearchHitHolder>
  );
}
